import React, { useEffect, useState } from "react";
import {
  GoogleAuthProvider,
  getRedirectResult,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { oceanGuideAdminSpots } from "./OceanGuidePage";
import {
  defaultOceanGuideSettings,
  getOceanGuideAdminText,
  OCEAN_GUIDE_SETTINGS_PATH,
  subscribeOceanGuideSpotOverrides,
  subscribeOceanGuideSettings,
} from "../services/oceanGuideSettings";
import s from "./OceanGuideAdminPage.module.scss";

const choices = [
  ["auto", "自動判定"],
  ["best", "◎ とても良い"],
  ["good", "○ 良い"],
  ["caution", "△ 注意"],
  ["bad", "× おすすめしない"],
];

const SelectField = ({ label, value, onChange }) => (
  <label className={s.field}>
    <span>{label}</span>
    <select value={value} onChange={(event) => onChange(event.target.value)}>
      {choices.map(([key, text]) => (
        <option key={key} value={key}>{text}</option>
      ))}
    </select>
  </label>
);

const weatherChoices = [
  ["auto", "自動判定"], ["晴れ", "☀ 晴れ"], ["晴時々曇", "🌤 晴時々曇"],
  ["晴れのち曇", "🌤 晴れのち曇"], ["曇時々晴", "⛅ 曇時々晴"],
  ["曇", "☁ 曇"], ["曇時々雨", "🌦 曇時々雨"], ["雨", "☔ 雨"],
];

const WeatherSelectField = ({ value, onChange }) => (
  <label className={s.field}>
    <span>表示する天気</span>
    <select value={value || "auto"} onChange={(event) => onChange(event.target.value)}>
      {weatherChoices.map(([key, text]) => <option key={key} value={key}>{text}</option>)}
    </select>
  </label>
);

const BulkSelectField = ({ label, onChange }) => (
  <label className={s.field}>
    <span>{label}</span>
    <select value="" onChange={(event) => onChange(event.target.value)}>
      <option value="" disabled>一括設定を選ぶ</option>
      {choices.map(([key, text]) => (
        <option key={key} value={key}>{text}</option>
      ))}
    </select>
  </label>
);

const ScoreField = ({ label, value, onChange }) => (
  <label className={s.field}>
    <span>{label}</span>
    <select value={value} onChange={(event) => onChange(Number(event.target.value))}>
      <option value="-1">自動判定</option>
      {[0, 1, 2, 3, 4, 5].map((score) => (
        <option key={score} value={score}>{score}</option>
      ))}
    </select>
  </label>
);

const BestTimeEditor = ({ day, values, updateRating, textValue, updateText }) => (
  <div>
    <strong>ベストタイム</strong>
    {[["Drone", "drone", "ドローン撮影"], ["Snorkel", "snorkel", "シュノーケル"], ["Marine", "marine", "マリンアクティビティ"]].map(([field, key, label]) => (
      <div className={s.spotEditor} key={key}>
        <strong>{label}</strong>
        <SelectField label="評価" value={values[`${day}BestTimeRatings`]?.[key] || "auto"} onChange={(value) => updateRating(day, key, value)} />
        <label className={s.field}>
          <span>おすすめ時間（空欄なら自動）</span>
          <input placeholder="現在は自動判定" maxLength="80" value={textValue(`${day}${field}Time`)} onChange={(event) => updateText(`${day}${field}Time`, event.target.value)} />
        </label>
        <label className={s.field}>
          <span>説明文（空欄なら自動）</span>
          <textarea placeholder="現在は自動判定" maxLength="200" rows="3" value={textValue(`${day}${field}Text`)} onChange={(event) => updateText(`${day}${field}Text`, event.target.value)} />
        </label>
      </div>
    ))}
  </div>
);

const getLoginErrorMessage = (error) => {
  const messages = {
    "auth/operation-not-allowed": "Firebase AuthenticationでGoogleログインを有効にしてください",
    "auth/unauthorized-domain": `Firebase Authenticationの承認済みドメインに「${window.location.hostname}」を追加してください`,
    "auth/popup-blocked": "ブラウザがログイン画面をブロックしました。ポップアップを許可して、もう一度お試しください",
    "auth/popup-closed-by-user": "Googleログイン画面が閉じられました。もう一度お試しください",
    "auth/network-request-failed": "通信に失敗しました。接続を確認して、もう一度お試しください",
  };
  return messages[error?.code] || `ログインできませんでした（${error?.code || "原因不明"}）`;
};

const OceanGuideAdminPage = () => {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [values, setValues] = useState(defaultOceanGuideSettings);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [spotOverrides, setSpotOverrides] = useState({});
  const [textLanguage, setTextLanguage] = useState("JP");
  const [activeTab, setActiveTab] = useState("today");
  const [spotComments, setSpotComments] = useState({});
  const [threadsQuestions, setThreadsQuestions] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => onAuthStateChanged(auth, (nextUser) => {
    setUser(nextUser);
    setAuthReady(true);
  }), []);

  useEffect(() => {
    getRedirectResult(auth).catch((error) => {
      setMessage(getLoginErrorMessage(error));
    });
  }, []);

  useEffect(() => subscribeOceanGuideSettings((nextValues) => {
    setValues(nextValues);
    setSettingsLoaded(true);
  }, () => {
    setSettingsLoaded(true);
    setMessage("データを読み込めませんでした");
  }), []);

  useEffect(() => subscribeOceanGuideSpotOverrides(setSpotOverrides, () => {
    setMessage("場所別データを読み込めませんでした");
  }), []);

  useEffect(() => {
    if (activeTab !== "comments") return undefined;
    const unsubscribers = oceanGuideAdminSpots.map((spot) => onSnapshot(
      query(
        collection(db, "oceanGuideSpotComments", spot.id, "comments"),
        orderBy("createdAt", "desc"),
        limit(20),
      ),
      (snapshot) => setSpotComments((current) => ({
        ...current,
        [spot.id]: snapshot.docs.map((item) => ({ id: item.id, ...item.data() })),
      })),
      () => setMessage(`${spot.name}の投稿を読み込めませんでした`),
    ));
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "threads") return undefined;
    return onSnapshot(
      query(collection(db, "threadsTravelQuestions"), orderBy("collectedAt", "desc"), limit(50)),
      (snapshot) => setThreadsQuestions(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))),
      (error) => setMessage(`Threads質問を読み込めませんでした（${error.code || error.message}）`),
    );
  }, [activeTab]);

  const update = (key, value) => setValues((current) => ({ ...current, [key]: value }));
  const updateText = (key, value) => {
    if (textLanguage === "JP") {
      update(key, value);
      return;
    }
    const localeKey = { EN: "EN", 中文: "ZH", 한국어: "KO" }[textLanguage];
    setValues((current) => ({
      ...current,
      translations: {
        ...current.translations,
        [localeKey]: {
          ...current.translations?.[localeKey],
          [key]: value,
        },
      },
    }));
  };
  const textValue = (key) => getOceanGuideAdminText(values, key, textLanguage);
  const updateScore = (day, key, value) => setValues((current) => ({
    ...current,
    [day]: { ...current[day], [key]: value },
  }));
  const updateBestTimeRating = (day, key, value) => setValues((current) => ({
    ...current,
    [`${day}BestTimeRatings`]: {
      ...current[`${day}BestTimeRatings`],
      [key]: value,
    },
  }));
  const updateSpot = (spot, key, value) => setSpotOverrides((current) => ({
    ...current,
    [spot.id]: {
      name: spot.name,
      marineRating: "auto",
      beachRating: "auto",
      showCaution: false,
      todaySnorkelRecommended: false,
      todayBeachRecommended: false,
      tomorrowSnorkelRecommended: false,
      tomorrowBeachRecommended: false,
      ...current[spot.id],
      [key]: value,
    },
  }));
  const updateDirection = (direction, key, value) => setSpotOverrides((current) => {
    const next = { ...current };
    oceanGuideAdminSpots
      .filter((spot) => spot.direction === direction)
      .forEach((spot) => {
        next[spot.id] = {
          name: spot.name,
          marineRating: "auto",
          beachRating: "auto",
          showCaution: false,
          todaySnorkelRecommended: false,
          todayBeachRecommended: false,
          tomorrowSnorkelRecommended: false,
          tomorrowBeachRecommended: false,
          ...next[spot.id],
          [key]: value,
        };
      });
    return next;
  });

  const login = async () => {
    setMessage("");
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (error) {
      if (["auth/popup-blocked", "auth/cancelled-popup-request"].includes(error.code)) {
        try {
          await signInWithRedirect(auth, new GoogleAuthProvider());
          return;
        } catch (redirectError) {
          setMessage(getLoginErrorMessage(redirectError));
          return;
        }
      }
      setMessage(getLoginErrorMessage(error));
    }
  };

  const removeSpotComment = async (spotId, commentId) => {
    if (!window.confirm("この投稿を削除しますか？")) return;
    try {
      await deleteDoc(doc(db, "oceanGuideSpotComments", spotId, "comments", commentId));
      setMessage("投稿を削除しました");
    } catch (error) {
      setMessage(`投稿を削除できませんでした（${error.code || error.message}）`);
    }
  };

  const editThreadsQuestion = (id, key, value) => {
    setThreadsQuestions((current) => current.map((item) => (
      item.id === id ? { ...item, [key]: value } : item
    )));
  };

  const saveThreadsQuestion = async (item, status = item.status) => {
    try {
      await updateDoc(doc(db, "threadsTravelQuestions", item.id), {
        draftAnswer: item.draftAnswer || "",
        finalAnswer: item.finalAnswer || item.draftAnswer || "",
        category: item.category || "other",
        status,
        updatedAt: serverTimestamp(),
      });
      setMessage(status === "approved" ? "回答を承認し、音声案内用の知識として保存しました" : "保存しました");
    } catch (error) {
      setMessage(`Threads質問を保存できませんでした（${error.code || error.message}）`);
    }
  };

  const copyThreadsAnswer = async (item) => {
    try {
      await navigator.clipboard.writeText(item.finalAnswer || item.draftAnswer || "");
      setMessage("回答をコピーしました。Threadsで貼り付けられます");
    } catch {
      setMessage("回答をコピーできませんでした");
    }
  };

  const bulkRemoveSpotComments = async (olderThanDays = null) => {
    setBulkDeleting(true);
    setMessage("削除対象を確認しています...");
    try {
      const cutoff = olderThanDays === null
        ? null
        : Timestamp.fromMillis(Date.now() - olderThanDays * 24 * 60 * 60 * 1000);
      const snapshots = await Promise.all(oceanGuideAdminSpots.map((spot) => {
        const commentsRef = collection(db, "oceanGuideSpotComments", spot.id, "comments");
        return getDocs(cutoff
          ? query(commentsRef, where("createdAt", "<", cutoff))
          : commentsRef);
      }));
      const refs = snapshots.flatMap((snapshot) => snapshot.docs.map((item) => item.ref));
      if (refs.length === 0) {
        setMessage("削除対象の投稿はありません");
        return;
      }
      const rangeLabel = olderThanDays === null ? "すべての投稿" : `${olderThanDays}日より前の投稿`;
      if (!window.confirm(`${rangeLabel} ${refs.length}件を削除します。元に戻せません。よろしいですか？`)) {
        setMessage("削除をキャンセルしました");
        return;
      }
      for (let index = 0; index < refs.length; index += 450) {
        const batch = writeBatch(db);
        refs.slice(index, index + 450).forEach((ref) => batch.delete(ref));
        await batch.commit();
      }
      setMessage(`${refs.length}件の投稿を削除しました`);
    } catch (error) {
      setMessage(`一括削除できませんでした（${error.code || error.message}）`);
    } finally {
      setBulkDeleting(false);
    }
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const payload = Object.fromEntries(
        Object.entries(values).filter(([key]) => key !== "updatedAt"),
      );
      const batch = writeBatch(db);
      batch.set(doc(db, ...OCEAN_GUIDE_SETTINGS_PATH), {
        ...payload,
        updatedAt: serverTimestamp(),
      });
      Object.entries(spotOverrides).forEach(([id, override]) => {
        batch.set(doc(db, "oceanGuideSpotOverrides", id), {
          name: override.name,
          overrideDate: new Intl.DateTimeFormat("en-CA", {
            timeZone: "Asia/Tokyo",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }).format(new Date()),
          marineRating: override.marineRating || "auto",
          beachRating: override.beachRating || "auto",
          showCaution: Boolean(override.showCaution),
          todaySnorkelRecommended: Boolean(override.todaySnorkelRecommended),
          todayBeachRecommended: Boolean(override.todayBeachRecommended),
          tomorrowSnorkelRecommended: Boolean(override.tomorrowSnorkelRecommended),
          tomorrowBeachRecommended: Boolean(override.tomorrowBeachRecommended),
          updatedAt: serverTimestamp(),
        });
      });
      await batch.commit();
      setMessage("保存しました。サイトに反映されています");
    } catch (error) {
      console.error("Ocean Guide admin save failed", error, {
        settingsPayload: values,
        spotOverrides,
      });
      setMessage(error.code === "permission-denied"
        ? `このアカウントには保存権限がありません（${error.message}）`
        : `保存できませんでした（${error.code || error.message}）`);
    } finally {
      setSaving(false);
    }
  };

  if (!authReady) return <main className={s.root}>読み込み中...</main>;

  if (!user) {
    return (
      <main className={s.root}>
        <h1>Ocean Guide 管理</h1>
        <button className={s.primaryButton} type="button" onClick={login}>
          Googleでログイン
        </button>
        {message && <p className={s.message}>{message}</p>}
      </main>
    );
  }

  return (
    <main className={s.root}>
      <div className={s.topLine}>
        <h1>Ocean Guide 管理</h1>
        <button type="button" onClick={() => signOut(auth)}>ログアウト</button>
      </div>
      <p className={s.account}>{user.email}</p>
      <p className={s.account}>
        {settingsLoaded
          ? "現在保存されている内容を表示しています。空欄は自動判定の項目です。"
          : "保存済みデータを読み込み中..."}
      </p>
      <form onSubmit={save}>
        <nav className={s.tabs} aria-label="編集する項目">
          {[
            ["today", "今日"],
            ["tomorrow", "明日"],
            ["afterTomorrow", "明後日"],
            ["yabiji", "八重干瀬"],
            ["spots", "場所別"],
            ["comments", "みんなの声"],
            ["threads", "Threads質問"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={activeTab === key ? s.activeTab : ""}
              onClick={() => setActiveTab(key)}
            >
              {label}
            </button>
          ))}
        </nav>
        <label className={s.field}>
          <span>テキスト編集言語</span>
          <select value={textLanguage} onChange={(event) => setTextLanguage(event.target.value)}>
            <option value="JP">日本語</option>
            <option value="EN">English</option>
            <option value="中文">中文</option>
            <option value="한국어">한국어</option>
          </select>
        </label>
        {activeTab === "today" && <fieldset>
          <legend>今日</legend>
          <h2 className={s.sectionTitle}>1. トップ表示</h2>
          <div className={s.spotEditor}>
            <strong>トップの緊急お知らせ</strong>
            <label className={s.checkField}>
              <input type="checkbox" checked={Boolean(values.topAlertEnabled)} onChange={(event) => update("topAlertEnabled", event.target.checked)} />
              お知らせを表示する
            </label>
            <label className={s.field}>
              <span>お知らせ文</span>
              <textarea maxLength="240" rows="4" value={textValue("topAlertText")} onChange={(event) => updateText("topAlertText", event.target.value)} />
            </label>
          </div>
          <div className={s.spotEditor}>
            <strong>ホーム・予報画面の台風情報</strong>
            <label className={s.checkField}>
              <input
                type="checkbox"
                checked={values.forecastTyphoonVisible !== false}
                onChange={(event) => update("forecastTyphoonVisible", event.target.checked)}
              />
              宮古島に影響がありそうな台風がある場合に表示する
            </label>
          </div>
          <label className={s.field}>
            <span>トップ見出し（空欄なら自動）</span>
            <input placeholder="現在は自動判定" maxLength="80" value={textValue("todayTitle")} onChange={(event) => updateText("todayTitle", event.target.value)} />
          </label>
          <label className={s.field}>
            <span>トップの補足文（空欄なら自動）</span>
            <textarea maxLength="160" rows="3" value={textValue("todayNote")} onChange={(event) => updateText("todayNote", event.target.value)} />
          </label>
          <h2 className={s.sectionTitle}>2. 総合評価・天気・海</h2>
          <WeatherSelectField value={values.todayWeather} onChange={(value) => update("todayWeather", value)} />
          <SelectField label="総合評価（◎・○・△・×）" value={values.todayRating} onChange={(value) => update("todayRating", value)} />
          <label className={s.field}>
            <span>総合評価の説明文（空欄なら自動）</span>
            <textarea placeholder="現在は自動判定" maxLength="200" rows="3" value={textValue("todayAssessmentText")} onChange={(event) => updateText("todayAssessmentText", event.target.value)} />
          </label>
          <label className={s.field}>
            <span>予報の天気説明文（空欄なら自動）</span>
            <textarea placeholder="現在は自動判定" maxLength="200" rows="3" value={textValue("todayWeatherSummary")} onChange={(event) => updateText("todayWeatherSummary", event.target.value)} />
          </label>
          <SelectField label="海の透明度（◎・○・△・×）" value={values.todayClarity} onChange={(value) => update("todayClarity", value)} />
          <h2 className={s.sectionTitle}>3. 日別おすすめ度（0〜5）</h2>
          <div className={s.scoreGrid}>
            <ScoreField label="シュノーケル" value={values.todayScores.snorkel} onChange={(value) => updateScore("todayScores", "snorkel", value)} />
            <ScoreField label="海遊び" value={values.todayScores.marine} onChange={(value) => updateScore("todayScores", "marine", value)} />
            <ScoreField label="撮影" value={values.todayScores.photo} onChange={(value) => updateScore("todayScores", "photo", value)} />
          </div>
          <h2 className={s.sectionTitle}>4. おすすめの時間</h2>
          <BestTimeEditor day="today" values={values} updateRating={updateBestTimeRating} textValue={textValue} updateText={updateText} />
          <h2 className={s.sectionTitle}>5. 夕方・夜</h2>
          <SelectField label="サンセット評価" value={values.todaySunsetRating} onChange={(value) => update("todaySunsetRating", value)} />
          <SelectField label="星空評価（一括）" value={values.todayStarRating} onChange={(value) => update("todayStarRating", value)} />
          <label className={s.field}>
            <span>夕焼け判定文（例：今日の空は焼けます）</span>
            <input maxLength="80" value={textValue("todaySunsetText")} onChange={(event) => updateText("todaySunsetText", event.target.value)} />
          </label>
        </fieldset>}

        {activeTab === "tomorrow" && <fieldset>
          <legend>明日</legend>
          <h2 className={s.sectionTitle}>1. トップ表示</h2>
          <label className={s.field}>
            <span>トップ見出し（空欄なら自動）</span>
            <input placeholder="現在は自動判定" maxLength="80" value={textValue("tomorrowTitle")} onChange={(event) => updateText("tomorrowTitle", event.target.value)} />
          </label>
          <label className={s.field}>
            <span>トップの補足文（空欄なら自動）</span>
            <textarea maxLength="160" rows="3" value={textValue("tomorrowNote")} onChange={(event) => updateText("tomorrowNote", event.target.value)} />
          </label>
          <h2 className={s.sectionTitle}>2. 総合評価・天気・海</h2>
          <WeatherSelectField value={values.tomorrowWeather} onChange={(value) => update("tomorrowWeather", value)} />
          <SelectField label="総合評価（◎・○・△・×）" value={values.tomorrowRating} onChange={(value) => update("tomorrowRating", value)} />
          <label className={s.field}>
            <span>総合評価の説明文（空欄なら自動）</span>
            <textarea placeholder="現在は自動判定" maxLength="200" rows="3" value={textValue("tomorrowAssessmentText")} onChange={(event) => updateText("tomorrowAssessmentText", event.target.value)} />
          </label>
          <label className={s.field}>
            <span>予報の天気説明文（空欄なら自動）</span>
            <textarea placeholder="現在は自動判定" maxLength="200" rows="3" value={textValue("tomorrowWeatherSummary")} onChange={(event) => updateText("tomorrowWeatherSummary", event.target.value)} />
          </label>
          <SelectField label="海の透明度（◎・○・△・×）" value={values.tomorrowClarity} onChange={(value) => update("tomorrowClarity", value)} />
          <h2 className={s.sectionTitle}>3. 日別おすすめ度（0〜5）</h2>
          <div className={s.scoreGrid}>
            <ScoreField label="シュノーケル" value={values.tomorrowScores.snorkel} onChange={(value) => updateScore("tomorrowScores", "snorkel", value)} />
            <ScoreField label="海遊び" value={values.tomorrowScores.marine} onChange={(value) => updateScore("tomorrowScores", "marine", value)} />
            <ScoreField label="撮影" value={values.tomorrowScores.photo} onChange={(value) => updateScore("tomorrowScores", "photo", value)} />
          </div>
          <h2 className={s.sectionTitle}>4. おすすめの時間</h2>
          <BestTimeEditor day="tomorrow" values={values} updateRating={updateBestTimeRating} textValue={textValue} updateText={updateText} />
          <h2 className={s.sectionTitle}>5. 夕方・夜</h2>
          <SelectField label="サンセット評価" value={values.tomorrowSunsetRating} onChange={(value) => update("tomorrowSunsetRating", value)} />
          <SelectField label="星空評価（一括）" value={values.tomorrowStarRating} onChange={(value) => update("tomorrowStarRating", value)} />
          <label className={s.field}>
            <span>夕焼け判定文（例：明日の空は焼けそうです）</span>
            <input maxLength="80" value={textValue("tomorrowSunsetText")} onChange={(event) => updateText("tomorrowSunsetText", event.target.value)} />
          </label>
        </fieldset>}

        {activeTab === "afterTomorrow" && <fieldset>
          <legend>明後日</legend>
          <h2 className={s.sectionTitle}>1. 総合評価・天気・海</h2>
          <WeatherSelectField value={values.dayAfterTomorrowWeather} onChange={(value) => update("dayAfterTomorrowWeather", value)} />
          <SelectField label="総合評価（◎・○・△・×）" value={values.dayAfterTomorrowRating} onChange={(value) => update("dayAfterTomorrowRating", value)} />
          <label className={s.field}>
            <span>総合評価の説明文（空欄なら自動）</span>
            <textarea placeholder="現在は自動判定" maxLength="200" rows="3" value={textValue("dayAfterTomorrowAssessmentText")} onChange={(event) => updateText("dayAfterTomorrowAssessmentText", event.target.value)} />
          </label>
          <label className={s.field}>
            <span>予報の天気説明文（空欄なら自動）</span>
            <textarea placeholder="現在は自動判定" maxLength="200" rows="3" value={textValue("dayAfterTomorrowWeatherSummary")} onChange={(event) => updateText("dayAfterTomorrowWeatherSummary", event.target.value)} />
          </label>
          <SelectField label="海の透明度（◎・○・△・×）" value={values.dayAfterTomorrowClarity} onChange={(value) => update("dayAfterTomorrowClarity", value)} />
          <h2 className={s.sectionTitle}>2. 日別おすすめ度（0〜5）</h2>
          <div className={s.scoreGrid}>
            <ScoreField label="シュノーケル" value={values.dayAfterTomorrowScores.snorkel} onChange={(value) => updateScore("dayAfterTomorrowScores", "snorkel", value)} />
            <ScoreField label="海遊び" value={values.dayAfterTomorrowScores.marine} onChange={(value) => updateScore("dayAfterTomorrowScores", "marine", value)} />
            <ScoreField label="撮影" value={values.dayAfterTomorrowScores.photo} onChange={(value) => updateScore("dayAfterTomorrowScores", "photo", value)} />
          </div>
          <h2 className={s.sectionTitle}>3. おすすめの時間</h2>
          <BestTimeEditor day="dayAfterTomorrow" values={values} updateRating={updateBestTimeRating} textValue={textValue} updateText={updateText} />
        </fieldset>}

        {activeTab === "yabiji" && <fieldset>
          <legend>八重干瀬</legend>
          <label className={s.field}>
            <span>今日の案内文</span>
            <textarea maxLength="300" rows="6" value={textValue("todayYabijiText")} onChange={(event) => updateText("todayYabijiText", event.target.value)} />
          </label>
          <label className={s.field}>
            <span>明日の案内文</span>
            <textarea maxLength="300" rows="6" value={textValue("tomorrowYabijiText")} onChange={(event) => updateText("tomorrowYabijiText", event.target.value)} />
          </label>
        </fieldset>}

        {activeTab === "spots" && <fieldset>
          <legend>場所別：シュノーケル・ビーチ</legend>
          {["北向き", "東向き", "南向き", "西向き", "方角不明"].map((direction) => {
            const group = oceanGuideAdminSpots.filter((spot) => spot.direction === direction);
            if (group.length === 0) return null;
            return (
              <section className={s.directionGroup} key={direction}>
                <h2>{direction}</h2>
                <div className={s.directionBulkEditor}>
                  <strong>{direction}を一括変更</strong>
                  <BulkSelectField label="海遊び評価" onChange={(value) => updateDirection(direction, "marineRating", value)} />
                  <BulkSelectField label="ビーチ遊び評価" onChange={(value) => updateDirection(direction, "beachRating", value)} />
                  <div className={s.bulkButtons}>
                    <button type="button" onClick={() => updateDirection(direction, "showCaution", true)}>注意ラベルを全て表示</button>
                    <button type="button" onClick={() => updateDirection(direction, "showCaution", false)}>注意ラベルを全て解除</button>
                  </div>
                </div>
                {group.map((spot) => {
                  const override = {
                    marineRating: "auto",
                    beachRating: "auto",
                    showCaution: false,
                    todaySnorkelRecommended: false,
                    todayBeachRecommended: false,
                    tomorrowSnorkelRecommended: false,
                    tomorrowBeachRecommended: false,
                    ...spotOverrides[spot.id],
                  };
                  return (
                    <div className={s.spotEditor} key={spot.id}>
                      <strong>{spot.name}</strong>
                      <SelectField label="今日の海遊び" value={override.marineRating} onChange={(value) => updateSpot(spot, "marineRating", value)} />
                      <SelectField label="今日のビーチ遊び" value={override.beachRating} onChange={(value) => updateSpot(spot, "beachRating", value)} />
                      <label className={s.checkField}>
                        <input type="checkbox" checked={override.showCaution} onChange={(event) => updateSpot(spot, "showCaution", event.target.checked)} />
                        注意が必要ラベルを表示
                      </label>
                    </div>
                  );
                })}
              </section>
            );
          })}
        </fieldset>}

        {activeTab === "comments" && <fieldset>
          <legend>みんなの声・投稿管理</legend>
          <p className={s.commentAdminLead}>各地点の最新20件を表示しています。不適切な投稿は個別に削除できます。</p>
          <section className={s.commentBulkDelete}>
            <strong>古い投稿をまとめて整理</strong>
            <p>対象件数を確認してから削除します。削除した投稿は元に戻せません。</p>
            <div>
              <button type="button" disabled={bulkDeleting} onClick={() => bulkRemoveSpotComments(7)}>7日より前を削除</button>
              <button type="button" disabled={bulkDeleting} onClick={() => bulkRemoveSpotComments(30)}>30日より前を削除</button>
              <button type="button" disabled={bulkDeleting} onClick={() => bulkRemoveSpotComments(null)}>全投稿を削除</button>
            </div>
          </section>
          {oceanGuideAdminSpots.flatMap((spot) => (
            spotComments[spot.id] || []
          ).map((item) => (
              <article className={s.commentAdminCard} key={`${spot.id}-${item.id}`}>
                <div className={s.commentAdminHead}>
                  <strong>{spot.name}</strong>
                  <time>{item.observedDate || "日付なし"} {item.observedTime || ""}</time>
                </div>
                <small>{item.name || "匿名"}</small>
                <p>{item.text}</p>
                <div className={s.commentAdminRatings}>
                  {item.crowd && item.crowd !== "none" && <span>混雑：{item.crowd}</span>}
                  {item.beachRating && item.beachRating !== "none" && <span>ビーチル：{item.beachRating}</span>}
                  {item.snorkelRating && item.snorkelRating !== "none" && <span>シュノーケル：{item.snorkelRating}</span>}
                  {item.windStrength > 0 && <span>風：{item.windStrength}/5</span>}
                  {item.waveStrength > 0 && <span>波：{item.waveStrength}/5</span>}
                </div>
                <button type="button" onClick={() => removeSpotComment(spot.id, item.id)}>削除</button>
              </article>
            ))
          )}
          {Object.values(spotComments).every((items) => !items?.length) && (
            <p className={s.commentAdminEmpty}>投稿はまだありません。</p>
          )}
        </fieldset>}

        {activeTab === "threads" && <fieldset>
          <legend>Threadsの宮古島観光質問</legend>
          <p className={s.commentAdminLead}>
            6時間ごとに質問候補を収集します。承認した回答は、将来の宮古島音声案内で使う知識として蓄積されます。
          </p>
          <div className={s.threadsStatusSummary}>
            <span>新着 {threadsQuestions.filter((item) => item.status === "candidate").length}</span>
            <span>承認済み {threadsQuestions.filter((item) => item.status === "approved").length}</span>
            <span>対象外 {threadsQuestions.filter((item) => item.status === "ignored").length}</span>
          </div>
          {threadsQuestions.map((item) => (
            <article className={s.threadsQuestionCard} key={item.id}>
              <div className={s.commentAdminHead}>
                <strong>{item.category || "other"}</strong>
                <span className={s.threadsStatus}>{item.status || "candidate"}</span>
              </div>
              <p className={s.threadsQuestionText}>{item.question}</p>
              <small>@{item.username || "unknown"}・{item.sourceTimestamp || "日時不明"}</small>
              <label className={s.field}>
                <span>回答</span>
                <textarea
                  rows="5"
                  maxLength="1000"
                  value={item.finalAnswer || item.draftAnswer || ""}
                  onChange={(event) => editThreadsQuestion(item.id, "finalAnswer", event.target.value)}
                />
              </label>
              <div className={s.threadsQuestionActions}>
                <button type="button" onClick={() => saveThreadsQuestion(item, "approved")}>承認して蓄積</button>
                <button type="button" onClick={() => saveThreadsQuestion(item, "draft")}>下書き保存</button>
                <button type="button" onClick={() => copyThreadsAnswer(item)}>回答をコピー</button>
                {item.permalink && <a href={item.permalink} target="_blank" rel="noreferrer">Threadsで開く</a>}
                <button type="button" onClick={() => saveThreadsQuestion(item, "ignored")}>対象外</button>
              </div>
            </article>
          ))}
          {threadsQuestions.length === 0 && (
            <p className={s.commentAdminEmpty}>質問候補はまだありません。定期取得後にここへ表示されます。</p>
          )}
        </fieldset>}

        {!(["comments", "threads"].includes(activeTab)) && (
          <button className={s.primaryButton} type="submit" disabled={saving || !settingsLoaded}>
            {saving ? "保存中..." : "保存する"}
          </button>
        )}
      </form>
      {message && <p className={s.message}>{message}</p>}
      <a className={s.previewLink} href="/ocean-guide" target="_blank" rel="noreferrer">
        公開画面を確認
      </a>
    </main>
  );
};

export default OceanGuideAdminPage;
